
import axios from "axios";
import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS, BASE_URLS } from "../../../../core/data/redux/apis/urls";
import { tokenService } from "../../../../core/utils/tokenService";

/* ───────────────────────────── Types ───────────────────────────── */
export interface PaystubItem {
  sequenceNumber?: string;
  type?: string;
  paymentDate?: string;
  grossAmount?: string;
  paymentAmount?: string;
  paymentType?: string;
  [key: string]: any;
}

/* ───────────────────────────── Helpers ───────────────────────────── */
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const YEAR_OPTIONS = ["2024", "2025", "2026"];
export const MONTH_OPTIONS = MONTH_LABELS;


export function getLoggedInEmpId(): string {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user?.empId || user?.details?.empId || user?.usrid || "");
  } catch {
    return "";
  }
}

/** Pad a number to 2 digits. */
const pad2 = (n: number) => String(n).padStart(2, "0");

/** Compute SAP-style ISO start/end for a given year + 1-based month. */
export function getMonthStartEndDates(
  year: number,
  month: number
): { startDate: string; endDate: string } {
  const startDate = `${year}-${pad2(month)}-01T00:00:00`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${pad2(month)}-${pad2(lastDay)}T00:00:00`;
  return { startDate, endDate };
}

function sanitizeBase64(content: any): string | null {
  if (!content || typeof content !== "string") return null;
  let cleaned = content.trim();
  const commaIdx = cleaned.indexOf("base64,");
  if (commaIdx !== -1) cleaned = cleaned.slice(commaIdx + "base64,".length);
  cleaned = cleaned.replace(/\s/g, "");
  return cleaned || null;
}

function formatAmount(value: string | number | undefined): string {
  if (value === undefined || value === null || value === "") return "";
  const num = typeof value === "number" ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPayDate(isoDate: string | undefined): string {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return String(isoDate);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function formatPaystubItem(item: PaystubItem): PaystubItem {
  const currency = item.Currency ?? item.currency ?? "INR";
  const grossPay = item.GrossPay ?? item.grossPay ?? item.grossAmount ?? "";
  const amount = item.Amount ?? item.amount ?? item.paymentAmount ?? "";
  const payDate = item.PayDate ?? item.payDate ?? item.paymentDate ?? "";
  const reason =
    item.Reason ?? item.reason ?? item.type ?? item.paymentType ?? "Salary Payment";
  const seq =
    item.SEQUENCENUMBER ?? item.sequenceNumber ?? item.sequence ?? item.id;
  return {
    ...item,
    type: reason,
    paymentDate: formatPayDate(payDate),
    grossAmount: grossPay ? `${formatAmount(grossPay)} ${currency}` : "",
    paymentAmount: amount ? `${formatAmount(amount)} ${currency}` : "",
    sequenceNumber: seq != null ? String(seq) : undefined,
  };
}

export function base64ToObjectUrl(base64: string): string {
  const clean = sanitizeBase64(base64);
  if (!clean) throw new Error("Invalid PDF content");
  const byteChars = atob(clean);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const idx = result.indexOf("base64,");
      resolve(idx !== -1 ? result.slice(idx + "base64,".length) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function openBase64Pdf(base64: string, _fileName?: string): void {
  const url = base64ToObjectUrl(base64);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function downloadBase64Pdf(base64: string, fileName: string): void {
  const url = base64ToObjectUrl(base64);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* ───────────────────────────── API calls ───────────────────────────── */

export async function findAllPaystubs(params: {
  year: number;
  month: number;
  empId?: string;
}): Promise<PaystubItem[]> {
  const res: any = await Api.post(API_URLS.PAYSTUB.FIND_ALL, {
    year: params.year,
    month: params.month,
    // Scope the list to the logged-in employee so the sequence numbers we later
    // use for the English download belong to the correct person. Without this
    // the backend can return another employee's paystubs (wrong PDF on download).
    ...(params.empId ? { empId: params.empId } : {}),
    action: "SalaryFindAll",
  });
  const root = res?.data ?? res;
  const raw = root?.data ?? root;
  const list: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.list)
    ? raw.list
    : Array.isArray(raw?.paystubs)
    ? raw.paystubs
    : [];
  return list.map((x: PaystubItem) => formatPaystubItem(x));
}

export async function syncPaystubs(empId: string): Promise<any> {
  return Api.post(API_URLS.PAYSTUB.SYNC, { empId, action: "SalarySync" });
}

export interface PdfResult {
  base64: string;
  fileName: string;
}


export async function fetchEnglishPdf(params: {
  sequenceNumber: string;
  empId: string;
}): Promise<PdfResult> {
  const { sequenceNumber, empId } = params;
  const token = tokenService.getToken();
  const authHeader = token
    ? token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
    : undefined;

  const body = {
    Header: { device_type: "WEB" },
    sequenceNumber,
    personnelAssignment: empId,
  };

  const response = await axios.post(API_URLS.PAYSTUB.DOWNLOAD, body, {
    headers: {
      "Content-Type": "application/json",
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    timeout: 50000,
    responseType: "blob",
  });

  const blob: Blob = response.data;
  const contentType = (blob.type || "").toLowerCase();
  const fileName = `paystub_${sequenceNumber}_english.pdf`;

  if (
    contentType.includes("application/pdf") ||
    contentType.includes("application/octet-stream")
  ) {
    const base64 = await blobToBase64(blob);
    return { base64, fileName };
  }

  // Non-PDF: likely JSON with base64 content
  const text = await blob.text();
  let parsed: any = {};
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Invalid response from server");
  }
  const resData = parsed?.data ?? parsed;
  const data = resData?.data ?? resData;
  const content =
    data?.Content ??
    data?.content ??
    data?.FileContent ??
    data?.fileContent ??
    data?.pdfBase64 ??
    data?.base64 ??
    data?.file ??
    (typeof resData === "string" ? resData : undefined) ??
    (typeof data === "string" ? data : undefined);
  if (!content) {
    throw new Error(
      parsed?.message ?? parsed?.data?.message ?? "No file content in response."
    );
  }
  const clean = sanitizeBase64(content);
  if (!clean) throw new Error("Invalid PDF content format");
  return { base64: clean, fileName };
}


function fileNameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const raw = match?.[1]?.replace(/['"]/g, "").trim();
  return raw || fallback;
}

async function parseBlobError(blob: Blob, fallback = "Download failed"): Promise<never> {
  const text = await blob.text();
  try {
    const parsed = JSON.parse(text);
    throw new Error(
      parsed?.message ?? parsed?.data?.message ?? parsed?.data?.data?.message ?? fallback
    );
  } catch (err: any) {
    if (err instanceof SyntaxError) {
      throw new Error(fallback);
    }
    throw err instanceof Error ? err : new Error(fallback);
  }
}

export async function fetchMultiplePayslipPdf(params: {
  fromMonth: number;
  fromYear: number;
  toMonth: number;
  toYear: number;
  empId: string;
}): Promise<PdfResult> {
  const { fromMonth, fromYear, toMonth, toYear, empId } = params;
  const token = tokenService.getToken();
  const authHeader = token
    ? token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
    : undefined;

  const fromMonthName = MONTH_LABELS[fromMonth - 1]?.substring(0, 3) ?? String(fromMonth);
  const toMonthName = MONTH_LABELS[toMonth - 1]?.substring(0, 3) ?? String(toMonth);
  const defaultFileName = `multiple_payslips_${fromMonthName}${fromYear}_to_${toMonthName}${toYear}.pdf`;

  const body = await Api.generateBody(
    {
      fromMonth,
      fromYear,
      toMonth,
      toYear,
      empId,
      action: "MultiplePayslipDownload",
    },
    { "enc-req": 0, "enc-res": 0 },
    API_URLS.PAYSTUB.DOWNLOAD_STATEMENT
  );

  let response: any;
  try {
    response = await axios.post(API_URLS.PAYSTUB.DOWNLOAD_STATEMENT, body, {
      headers: {
        "Content-Type": "application/json",
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      timeout: 90000,
      responseType: "blob",
    });
  } catch (err: any) {
    const errBlob = err?.response?.data;
    if (errBlob instanceof Blob) {
      await parseBlobError(errBlob, err?.message || "Download failed");
    }
    throw new Error(
      err?.response?.data?.message ??
        err?.message ??
        "Download failed"
    );
  }

  const blob: Blob = response.data;
  const headerType = String(
    response?.headers?.["content-type"] || blob.type || ""
  ).toLowerCase();
  const isPdfBinary =
    (headerType.includes("application/pdf") ||
      headerType.includes("application/octet-stream")) &&
    blob.size > 0;

  let fileName = fileNameFromDisposition(
    response?.headers?.["content-disposition"],
    defaultFileName
  );

  if (isPdfBinary) {
    const base64 = await blobToBase64(blob);
    return { base64, fileName };
  }

  const text = await blob.text();
  let parsed: any = {};
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Invalid response from server");
  }

  const resData = parsed?.data ?? parsed;
  if (
    (parsed?.data?.status === 400 && parsed?.data?.message) ||
    (parsed?.status === 400 && parsed?.message)
  ) {
    throw new Error(parsed?.data?.message ?? parsed?.message ?? "Download failed");
  }

  const data = resData?.data ?? resData;
  const content =
    data?.Content ??
    data?.content ??
    data?.FileContent ??
    data?.fileContent ??
    data?.pdfBase64 ??
    data?.base64 ??
    data?.file ??
    data?.data ??
    (typeof resData === "string" ? resData : undefined) ??
    (typeof data === "string" ? data : undefined);

  const fromJsonFileName =
    data?.FileName ??
    data?.fileName ??
    data?.name ??
    data?.documentName;
  if (fromJsonFileName) fileName = String(fromJsonFileName);

  if (!content) {
    throw new Error(
      parsed?.message ?? parsed?.data?.message ?? "No file content in response."
    );
  }
  const clean = sanitizeBase64(content);
  if (!clean) throw new Error("Invalid PDF content format");
  return { base64: clean, fileName };
}

export async function fetchHindiPdf(params: {
  empId: string;
  year: number;
  month: number;
  monthName?: string;
  sequenceNumber?: string;
}): Promise<PdfResult> {
  const { empId, year, month, monthName, sequenceNumber } = params;
  const { startDate, endDate } = getMonthStartEndDates(year, month);
  const cleanEmpId = empId.replace(/^0+/, "");

  const res: any = await Api.post(API_URLS.PAYSTUB.DOWNLOAD_HINDI, {
    startDate,
    endDate,
    empId: cleanEmpId,
    action: "SalaryDownloadHindi",
  });

  const responseData = res?.data ?? res;
  let pdfContent: any = null;
  if (Array.isArray(responseData?.data?.d?.results)) {
    pdfContent = responseData.data.d.results[0]?.Content;
  } else if (Array.isArray(responseData?.d?.results)) {
    pdfContent = responseData.d.results[0]?.Content;
  } else if (Array.isArray(responseData?.results)) {
    pdfContent = responseData.results[0]?.Content;
  } else if (responseData?.Content) {
    pdfContent = responseData.Content;
  }

  if (!pdfContent) throw new Error("No PDF content found in response");
  const clean = sanitizeBase64(pdfContent);
  if (!clean) throw new Error("Invalid PDF content format");
  const suffix = monthName ? `_${monthName}_${year}` : "";
  const fileName = `paystub_${sequenceNumber ?? "statement"}_hindi${suffix}.pdf`;
  return { base64: clean, fileName };
}

export const BASE_URL = BASE_URLS.BASE_URL;
